import {
    ChangeDetectionStrategy,
    Component,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder, Validators } from '@angular/forms';
import {
    DialogButtonComponent,
    DialogButtonsComponent,
    DialogComponent,
    FormFieldComponent,
    InputTextComponent,
    RequiredLegendComponent,
} from '@interacta-shared/ui';
import { AI_CONVO_TITLE_MAX_LENGTH } from '@modules/ai-convo/models/ai-convo-history.model';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'interacta-ai-convo-rename-dialog',
    templateUrl: './ai-convo-rename-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        DialogButtonComponent,
        DialogButtonsComponent,
        DialogComponent,
        FormFieldComponent,
        InputTextComponent,
        TranslatePipe,
        RequiredLegendComponent,
    ],
})
export class AIConvoRenameDialogComponent {
    private readonly nnfb = inject(NonNullableFormBuilder);

    readonly title = input.required<string>();
    readonly isOpen = input(false);

    readonly editTitle = output<string>();
    readonly closing = output<void>();

    readonly titleControl = this.nnfb.control('', {
        validators: [
            Validators.required,
            Validators.maxLength(AI_CONVO_TITLE_MAX_LENGTH),
        ],
    });

    readonly inputFocused = signal(false);

    constructor() {
        toObservable(this.isOpen)
            .pipe(takeUntilDestroyed())
            .subscribe((isOpen) => {
                if (isOpen) {
                    this.titleControl.setValue(this.title());
                    requestAnimationFrame(() => this.inputFocused.set(true));
                } else {
                    this.inputFocused.set(false);
                }
            });
    }

    saveTitle(event?: Event): void {
        // The form submits on Enter; without this the native submit reloads the
        // page and tears down the AI panel.
        event?.preventDefault();
        const trimmedTitle = this.titleControl.value.trim();
        this.titleControl.setValue(trimmedTitle);
        if (this.titleControl.valid) {
            if (trimmedTitle !== this.title())
                this.editTitle.emit(trimmedTitle);
            this.close();
        }
    }

    close(): void {
        this.titleControl.reset();
        this.closing.emit();
    }
}
